import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import usePremiumAccess from '../hooks/usePremiumAccess';
import { normalizeToolKey } from '../constants/toolKeys';
import { cn } from '../utils/classNames';

// Tool components
import AIChatCard from '../components/aiChat/AiChatCard';
import QuizzesTool from '../components/ScoutIQDashboard/tools/QuizzesTool';
import FlashcardsTool from '../components/ScoutIQDashboard/tools/FlashcardsTool';
import PodcastsTool from '../components/ScoutIQDashboard/tools/PodcastsTool';
import AudiobooksTool from '../components/ScoutIQDashboard/tools/AudiobooksTool';
import TextToSpeechTool from '../components/ScoutIQDashboard/tools/TextToSpeechTool';
import UpgradeModal from '../components/common/UpgradeModal';

// Sidebar history/library components
import ChatHistoryPanel from '../components/aiChat/ChatHistoryPanel';
import QuizHistoryPanel from '../components/common/QuizHistoryPanel';
import FlashcardHistoryPanel from '../components/common/FlashcardHistoryPanel';
import AudioLibraryPanel from '../components/common/AudioLibraryPanel';

// Hooks
import { useAiChat } from '../hooks/useAiChat';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { useData } from '../contexts/DataContext';

// API
import { DocumentApi } from '../services/api/DocumentApi';

// Styles
import './AIToolsPage.css';
import '../components/primitives/Button.css';

// Tool metadata for rendering sidebar buttons
const TOOLS = [
  {
    key: 'chat',
    label: 'Chat',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    ),
  },
  {
    key: 'quizzes',
    label: 'Quizzes',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
      </svg>
    ),
  },
  {
    key: 'flashcards',
    label: 'Flashcards',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M12 4v16"/><path d="M2 12h20"/>
      </svg>
    ),
  },
  {
    key: 'podcasts',
    label: 'Podcasts',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
      </svg>
    ),
  },
  {
    key: 'audiobooks',
    label: 'Audiobooks',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
      </svg>
    ),
  },
  {
    key: 'tts',
    label: 'Text to Speech',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
      </svg>
    ),
  },
];

const NEW_ITEM_LABELS = {
  chat: 'New chat',
  quizzes: 'New quiz',
  flashcards: 'New deck',
  podcasts: 'New podcast',
  audiobooks: 'New audiobook',
  tts: 'New speech',
};

const SEARCH_PLACEHOLDERS = {
  chat: 'Search chats...',
  quizzes: 'Search quizzes...',
  flashcards: 'Search decks...',
  podcasts: 'Search podcasts...',
  audiobooks: 'Search audiobooks...',
  tts: 'Text to Speech',
};

// LocalStorage key prefixes
const STORAGE_KEYS = {
  activeTool: 'aitools.activeTool',
  quizzes: 'toolStates.quizzes',
  flashcards: 'toolStates.flashcards',
  podcasts: 'toolStates.podcasts',
  audiobooks: 'toolStates.audiobooks',
};

/* -------- Free-tier gate screen -------- */
function ToolsGateScreen({ navigate, showUploadNudge, onDismissNudge, onClose }) {
  const [clickedIdx, setClickedIdx] = useState(null);

  const handleToolClick = (idx) => {
    if (clickedIdx !== null) return;
    setClickedIdx(idx);
    setTimeout(() => setClickedIdx(null), 900);
  };

  const tools = [
    { icon: 'chat', name: 'AI Chat', sub: 'Ask anything about your course', tier: 'basic', delay: 1 },
    { icon: 'flash', name: 'Flashcards', sub: 'Auto-generated from your notes', tier: 'basic', delay: 2 },
    { icon: 'quiz', name: 'Quizzes', sub: 'Test yourself before exams', tier: 'basic', delay: 3 },
    { icon: 'audio', name: 'Podcasts & Audiobooks', sub: 'Learn while walking or driving', tier: 'pro', delay: 4 },
  ];

  const toolIcons = {
    chat: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
    ),
    flash: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M12 4v16"/><path d="M2 12h20"/></svg>
    ),
    quiz: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
    ),
    audio: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
    ),
  };

  return (
    <div className="tools-gate">
      <div className="tools-gate__backdrop" />

      <div className="tools-gate__card">
        {/* Close button */}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="tools-gate__close"
            aria-label="Close"
            style={{
              position: 'absolute', top: '12px', right: '12px', zIndex: 1,
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-secondary, #999)', fontSize: '22px', lineHeight: 1,
              padding: '4px 8px', borderRadius: '4px',
            }}
          >
            ✕
          </button>
        )}
        {/* LEFT: Scout + emotional hook */}
        <div className="tools-gate__left">
          <div className="tools-gate__left-inner">
            <div className="tools-gate__bubble">
              <span>You don&rsquo;t need to reread everything.</span>
              <span>Upload your course and I&rsquo;ll break it down for you.</span>
              <span className="tools-gate__typing" aria-hidden="true">
                <span className="tools-gate__dot" />
                <span className="tools-gate__dot" />
                <span className="tools-gate__dot" />
              </span>
            </div>
            <div className="tools-gate__scout-wrap">
              <img
                src="/scout_images/scout_thinking.png"
                alt=""
                className="tools-gate__scout-img"
                draggable="false"
              />
            </div>
            {showUploadNudge && (
              <div className="tools-gate__upload-cta">
                <button
                  className="tools-gate__upload-btn"
                  onClick={() => navigate('/dashboard')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  Upload a course
                </button>
                <button
                  className="tools-gate__upload-later"
                  onClick={onDismissNudge}
                >
                  Maybe later
                </button>
              </div>
            )}
            {!showUploadNudge && (
              <p className="tools-gate__tagline">
                Most students don&rsquo;t study harder. They use tools.
              </p>
            )}
          </div>
        </div>

        {/* RIGHT: Locked tool previews + CTA */}
        <div className="tools-gate__right">
          <h2 className="tools-gate__title">Your study toolkit</h2>
          <p className="tools-gate__subtitle">Everything you need to stay ahead &mdash; ready when you are.</p>

          <div className="tools-gate__tools">
            {tools.map((t, idx) => (
              <div
                key={t.icon}
                className={[
                  'tools-gate__tool',
                  `tools-gate__tool--${t.delay}`,
                  t.tier === 'pro' ? 'tools-gate__tool--pro' : '',
                  clickedIdx === idx ? 'tools-gate__tool--shimmer' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => handleToolClick(idx)}
              >
                <div className="tools-gate__tool-icon">
                  {toolIcons[t.icon]}
                </div>
                <div className="tools-gate__tool-text">
                  <span className="tools-gate__tool-name">{t.name}</span>
                  <span className="tools-gate__tool-sub">{t.sub}</span>
                </div>
                <span className={`tools-gate__tier-tag tools-gate__tier-tag--${t.tier}`}>
                  {t.tier === 'pro' ? 'Included in All-Gas' : 'Included with AI Tools'}
                </span>
                <span className="tools-gate__lock" aria-hidden="true">&#x1F512;</span>
              </div>
            ))}
          </div>

          <div className="tools-gate__ctas">
            <button
              className="tools-gate__cta-primary"
              onClick={() => navigate('/subscription')}
            >
              Start for $4.99/month
            </button>
            <p className="tools-gate__cta-includes">
              Includes AI chat, quizzes, and flashcards
            </p>

            <button
              className="tools-gate__cta-secondary"
              onClick={() => navigate('/subscription')}
            >
              Go All-Gas for $11.99/month
            </button>
            <p className="tools-gate__cta-secondary-sub">
              Includes audio learning tools
            </p>

            <p className="tools-gate__social-proof">
              Most students unlock this before their first exam.
            </p>

            <button
              className="tools-gate__dismiss"
              onClick={() => navigate('/dashboard')}
            >
              Not now &mdash; I&rsquo;ll do this manually
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * AIToolsPage - Unified AI Tools hub
 * 
 * Layout:
 * - Horizontal toolbar at top: Tool selector buttons
 * - Left sidebar (320px): History/library panel (tool-specific)
 * - Main content: Active tool component
 * 
 * Features:
 * - Lazy loads history data when tool becomes active
 * - Persists tool state to localStorage (separate keys per tool)
 * - Shares data via DataContext
 * - URL-driven tool selection (?tool=quizzes)
 */
export default function AIToolsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { preferences } = usePreferences();
  const { courses, isDashboardHydrated } = useData();
  const { updateConversationTitle } = useAiChat('all');
  
  // Contextual nudge for users without courses — only after dashboard data has fully loaded
  const dataReady = isDashboardHydrated && isDashboardHydrated();
  const hasCourses = Array.isArray(courses) && courses.length > 0;
  const [nudgeDismissed, setNudgeDismissed] = useState(() => {
    try { return localStorage.getItem('dh_tool_nudge_dismissed') === '1'; } catch { return false; }
  });
  const showNudge = dataReady && !hasCourses && !nudgeDismissed;

  // Debug: log nudge state
  if (process.env.NODE_ENV !== 'production') {
    console.debug('[AIToolsPage] dataReady:', dataReady, 'hasCourses:', hasCourses, 'nudgeDismissed:', nudgeDismissed, 'showNudge:', showNudge);
  }

  const dismissNudge = useCallback(() => {
    setNudgeDismissed(true);
    try { localStorage.setItem('dh_tool_nudge_dismissed', '1'); } catch {}
  }, []);

  // Subscription gating
  const { initialized } = useSubscription();
  const { canCreate, tier: currentTier, hasPremiumAccess } = usePremiumAccess();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [blockedFeature, setBlockedFeature] = useState({ name: '', tier: 'basic' });

  // URL query param determines active tool
  const urlParams = new URLSearchParams(location.search);
  const toolFromUrl = urlParams.get('tool') || 'chat';
  const activeTool = normalizeToolKey(toolFromUrl);

  // Tool states (persisted to localStorage)
  const [toolStates, setToolStates] = useState({});
  
  // Sidebar collapse state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('aitools.sidebarCollapsed')) || false;
    } catch {
      return false;
    }
  });
  
  // Quiz/Flashcard history states removed — panels fetch their own data internally
  
  // Audio player state for podcasts/audiobooks
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [selectedAudioItem, setSelectedAudioItem] = useState(null);
  
  // Audio library refresh key (incrementing triggers remount of sidebar AudioLibraryPanel)
  const [audioRefreshKey, setAudioRefreshKey] = useState(0);

  // Search mode state for pane search
  const [searchMode, setSearchMode] = useState(false);
  const [paneSearchQuery, setPaneSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const paneRef = useRef(null);
  
  // Track which tool history panels have been loaded (lazy loading)
  // (Panels now fetch their own data; keeping ref for potential future use)
  
  // Chat state for AIChatCard
  const [activeConversation, setActiveConversation] = useState(null);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [singleDocumentName, setSingleDocumentName] = useState(null);
  const [suppressAutoSelect, setSuppressAutoSelect] = useState(true);
  
  // Conversation persistence when switching tools
  const conversationPersistenceRef = useRef(null);

  // Load tool states from localStorage on mount
  // Clear fields that would prevent CREATE mode so tools always start fresh
  useEffect(() => {
    const loadedStates = {};
    Object.entries(STORAGE_KEYS).forEach(([key, storageKey]) => {
      if (key === 'activeTool') return; // Skip activeTool in toolStates object
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          loadedStates[key] = JSON.parse(stored);
        }
      } catch (e) {
        console.warn(`Failed to parse localStorage key ${storageKey}:`, e);
      }
    });

    // Reset podcasts/audiobooks to CREATE mode (clear generated result)
    if (loadedStates.podcasts) {
      loadedStates.podcasts = { ...loadedStates.podcasts, generatedAudio: null, showGenerator: false, isGenerating: false };
    }
    if (loadedStates.audiobooks) {
      loadedStates.audiobooks = { ...loadedStates.audiobooks, generatedAudio: null, showGenerator: false, isGenerating: false };
    }
    // Reset flashcards to CREATE mode
    if (loadedStates.flashcards) {
      loadedStates.flashcards = {
        ...loadedStates.flashcards,
        deck: null, cards: null, originalCards: null, activeIndex: 0,
        externalSelectedDeck: null, showGenerator: false, loading: false,
      };
    }
    // Reset quizzes to setup mode
    if (loadedStates.quizzes) {
      loadedStates.quizzes = {
        ...loadedStates.quizzes,
        quizResult: null, mode: 'setup', attemptId: null,
        currentIndex: 0, answersByIndex: {}, submitted: false,
        scoreBreakdown: null, verdictsByIndex: {}, externalSelectedQuiz: null,
        loading: false,
      };
    }

    setToolStates(loadedStates);
  }, []);

  // Save sidebar collapse state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('aitools.sidebarCollapsed', JSON.stringify(sidebarCollapsed));
    } catch (e) {
      console.warn('Failed to save sidebar collapse state:', e);
    }
  }, [sidebarCollapsed]);

  // Save tool state to localStorage when it changes
  const handleToolStateChange = useCallback((toolKey, state) => {
    setToolStates((prev) => {
      const updated = { ...prev, [toolKey]: state };
      // Persist to localStorage
      const storageKey = STORAGE_KEYS[toolKey];
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(state));
        } catch (e) {
          console.warn(`Failed to save ${toolKey} state to localStorage:`, e);
        }
      }
      return updated;
    });
  }, []);

  // Change active tool (updates URL)
  const handleToolChange = useCallback((newTool) => {
    const normalized = normalizeToolKey(newTool);
    
    navigate(`/ai-tools?tool=${normalized}`, { replace: true });
    // Save to localStorage for persistence
    localStorage.setItem(STORAGE_KEYS.activeTool, normalized);
  }, [navigate]);

  // Lazy load quiz history when quizzes tool becomes active
  // (QuizHistoryPanel now fetches its own data internally)

  // Listen for content-creation events to refresh sidebar history panels
  useEffect(() => {
    const handleAudioCreated = () => {
      // Increment key to remount sidebar AudioLibraryPanel, triggering fresh data load
      setAudioRefreshKey(k => k + 1);
    };

    window.addEventListener('audio:created', handleAudioCreated);
    return () => {
      window.removeEventListener('audio:created', handleAudioCreated);
    };
  }, []);

  // Handle quiz deletion
  // (no longer needed — detail view handles deletion internally)

  // Handle quiz selection — pass through to QuizzesTool via external selection
  const handleSelectQuiz = useCallback((quiz) => {
    handleToolStateChange('quizzes', {
      ...toolStates['quizzes'],
      externalSelectedQuiz: quiz,
    });
  }, [handleToolStateChange, toolStates]);

  // Handle flashcard deck selection
  const handleSelectFlashcardDeck = useCallback((deck) => {
    // Update FlashcardsTool state to show this deck
    handleToolStateChange('flashcards', {
      ...toolStates['flashcards'],
      externalSelectedDeck: deck,
    });
  }, [handleToolStateChange, toolStates]);

  // Handle new flashcard deck
  const handleNewFlashcardDeck = useCallback(() => {
    if (!canCreate('flashcards')) {
      setBlockedFeature({ name: 'Flashcard Generator', tier: 'basic' });
      setShowUpgradeModal(true);
      return;
    }
    handleToolStateChange('flashcards', {
      ...toolStates['flashcards'],
      externalSelectedDeck: null,
      showGenerator: true,
      deck: null,
      cards: null,
      originalCards: null,
      loading: false,
    });
  }, [handleToolStateChange, toolStates, canCreate]);

  // Handle new quiz
  const handleNewQuiz = useCallback(() => {
    if (!canCreate('quizzes')) {
      setBlockedFeature({ name: 'Quiz Generator', tier: 'basic' });
      setShowUpgradeModal(true);
      return;
    }
    handleToolStateChange('quizzes', {
      ...toolStates['quizzes'],
      quizResult: null,
      externalSelectedQuiz: null,
      mode: 'setup',
      attemptId: null,
      submitted: false,
      scoreBreakdown: null,
      verdictsByIndex: {},
      loading: false
    });
  }, [handleToolStateChange, toolStates, canCreate]);

  // (Flashcard rename/delete handlers removed — detail view handles these internally)

  // Persist conversation when switching away from chat, restore when returning
  useEffect(() => {
    if (activeTool === 'chat') {
      // Restore persisted conversation when returning to chat
      if (conversationPersistenceRef.current) {
        setActiveConversation(conversationPersistenceRef.current);
        conversationPersistenceRef.current = null;
      }
    } else {
      // Persist current conversation when switching away from chat
      if (activeConversation) {
        conversationPersistenceRef.current = activeConversation;
      }
    }
  }, [activeTool, activeConversation]);
  
  // Handle chat conversation selection from history
  const handleConversationSelected = useCallback((conversation) => {
    setActiveConversation(conversation);
    setSuppressAutoSelect(false); // Re-enable auto-select when user explicitly selects a conversation

    // Sync selectedDocumentIds to the new conversation's attachments so mode
    // switches correctly (e.g. leaving a single-doc chat should clear doc mode).
    const docIds = Array.isArray(conversation?.attachedDocumentIds)
      ? conversation.attachedDocumentIds.map(String)
      : [];
    setSelectedDocumentIds(docIds);

    // Reset cached document name; the effect will re-fetch if needed
    setSingleDocumentName(null);
  }, []);

  // Handle new chat creation
  const handleNewChat = useCallback(() => {
    if (!canCreate('chat')) {
      setBlockedFeature({ name: 'AI Chat', tier: 'basic' });
      setShowUpgradeModal(true);
      return;
    }
    setActiveConversation(null);
    setSingleDocumentName(null);
    setSelectedDocumentIds([]); // Clear selected documents for new chat
    setSuppressAutoSelect(true); // Prevent auto-selecting first conversation
  }, [canCreate]);
  
  // Detect single-document mode and fetch document name
  useEffect(() => {
    if (activeTool !== 'chat') return;
    
    if (selectedDocumentIds.length === 1) {
      // Fetch document name for the single document (needed if user later starts prompting)
      const docId = selectedDocumentIds[0];
      DocumentApi.getDocument(docId)
        .then(response => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('Failed to fetch document');
        })
        .then(doc => {
          setSingleDocumentName(doc.name);
          // Don't auto-resume document conversations here.
          // The user may still be adding more documents.
          // AiChatCard will handle conversation creation/resumption when the user sends the first prompt.
        })
        .catch(err => {
          console.error('Failed to fetch document name:', err);
          setSingleDocumentName(null);
        });
    } else {
      setSingleDocumentName(null);
    }
  }, [activeTool, selectedDocumentIds, user]);

  // Handle audio selection from library panel — pass through to tool
  const handleSelectAudio = useCallback((audioItem) => {
    setSelectedAudioItem(audioItem);
    setShowAudioPlayer(true);
  }, []);

  // Handle new audio creation
  const handleNewAudio = useCallback(() => {
    if (!canCreate(activeTool)) {
      setBlockedFeature({ name: activeTool === 'podcasts' ? 'Podcast Generator' : 'Audiobook Generator', tier: 'pro' });
      setShowUpgradeModal(true);
      return;
    }
    setShowAudioPlayer(false);
    setSelectedAudioItem(null);
    // Signal to show generator by passing a flag; also reset generation state
    setToolStates(prev => ({
      ...prev,
      [activeTool]: {
        ...prev[activeTool],
        showGenerator: true,
        isGenerating: false,
        generatedAudio: null,
      }
    }));
  }, [activeTool, canCreate]);

  // Unified "new item" handler based on active tool
  const handleNewItem = useCallback(() => {
    switch (activeTool) {
      case 'chat': handleNewChat(); break;
      case 'quizzes': handleNewQuiz(); break;
      case 'flashcards': handleNewFlashcardDeck(); break;
      case 'podcasts':
      case 'audiobooks': handleNewAudio(); break;
      default: break;
    }
  }, [activeTool, handleNewChat, handleNewQuiz, handleNewFlashcardDeck, handleNewAudio]);

  // Close search mode when switching tools
  useEffect(() => {
    setSearchMode(false);
    setPaneSearchQuery('');
  }, [activeTool]);

  // Escape key closes search mode
  useEffect(() => {
    if (!searchMode) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSearchMode(false);
        setPaneSearchQuery('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchMode]);

  // Auto-focus search input when search mode opens
  useEffect(() => {
    if (searchMode && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchMode]);

  // Render the appropriate history/library panel based on active tool
  const renderHistoryPanel = () => {
    const searchQuery = searchMode ? paneSearchQuery : undefined;

    switch (activeTool) {
      case 'chat':
        return (
          <ChatHistoryPanel
            onConversationSelected={handleConversationSelected}
            activeConversationId={activeConversation?.conversationId || activeConversation?.id}
            onNewChat={handleNewChat}
            suppressAutoSelectFirst={suppressAutoSelect}
            variant="aitools"
            mode="expanded"
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            sidebarCollapsed={sidebarCollapsed}
            externalSearchQuery={searchQuery}
          />
        );
      
      case 'quizzes':
        return (
          <QuizHistoryPanel
            onSelectQuiz={handleSelectQuiz}
            onNewQuiz={handleNewQuiz}
            showNewButton={false}
            hideHeader
            hideSearch
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            sidebarCollapsed={sidebarCollapsed}
            externalSearchQuery={searchQuery}
          />
        );
      
      case 'flashcards':
        return (
          <FlashcardHistoryPanel
            onSelectDeck={handleSelectFlashcardDeck}
            onNewDeck={handleNewFlashcardDeck}
            showNewButton={false}
            hideHeader
            hideSearch
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            sidebarCollapsed={sidebarCollapsed}
            externalSearchQuery={searchQuery}
          />
        );
      
      case 'podcasts':
        return (
          <AudioLibraryPanel
            key={`podcast-lib-${audioRefreshKey}`}
            format="podcast"
            onSelectItem={handleSelectAudio}
            onNewItem={handleNewAudio}
            hideHeader
            hideSearch
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            sidebarCollapsed={sidebarCollapsed}
            externalSearchQuery={searchQuery}
          />
        );
      
      case 'audiobooks':
        return (
          <AudioLibraryPanel
            key={`audiobook-lib-${audioRefreshKey}`}
            format="audiobook"
            onSelectItem={handleSelectAudio}
            onNewItem={handleNewAudio}
            hideHeader
            hideSearch
            onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            sidebarCollapsed={sidebarCollapsed}
            externalSearchQuery={searchQuery}
          />
        );
      
      default:
        return null;
    }
  };

  // Render the main tool component
  const renderToolContent = () => {
    // Wait for subscription to initialize before showing tools
    if (!initialized) {
      return null; // Keep gated during load - no spinner needed
    }

    switch (activeTool) {
      case 'chat':
        // Only switch to single-doc mode once the user has an active conversation.
        // Before the first prompt, keep standard mode so the user can freely add/remove docs.
        const isSingleDocMode = selectedDocumentIds.length === 1 && singleDocumentName && !!activeConversation;
        return (
          <AIChatCard
            variant="aitools"
            mode={isSingleDocMode ? "document" : "standard"}
            documentId={isSingleDocMode ? selectedDocumentIds[0] : undefined}
            documentName={isSingleDocMode ? singleDocumentName : undefined}
            conversation={activeConversation}
            onConversationActivated={setActiveConversation}
            onUpdateConversationTitle={(id, title) => updateConversationTitle(id, title)}
            externalSelectedDocuments={isSingleDocMode ? undefined : selectedDocumentIds}
            onExternalSelectedDocsChange={isSingleDocMode ? undefined : setSelectedDocumentIds}
          />
        );
      
      case 'quizzes':
        return (
          <QuizzesTool
            selectedCourse={null} // AI Tools page doesn't have course context
            isPanelExpanded={true}
            onClose={() => handleToolChange('chat')}
            persistedState={toolStates['quizzes']}
            onStateChange={(state) => handleToolStateChange('quizzes', state)}
          />
        );
      
      case 'flashcards':
        return (
          <FlashcardsTool
            selectedCourse={null}
            isExpanded={true}
            isPanelExpanded={true}
            onClose={() => handleToolChange('chat')}
            persistedState={toolStates['flashcards']}
            externalSelectedDeck={toolStates['flashcards']?.externalSelectedDeck}
            externalShowGenerator={toolStates['flashcards']?.showGenerator}
            onClearDeckSelection={() => {
              handleToolStateChange('flashcards', {
                ...toolStates['flashcards'],
                externalSelectedDeck: null,
                showGenerator: false,
              });
            }}
            onStateChange={(state) => handleToolStateChange('flashcards', state)}
          />
        );
      
      case 'podcasts':
        return (
          <PodcastsTool
            selectedCourse={null}
            isExpanded={true}
            isPanelExpanded={true}
            onClose={() => handleToolChange('chat')}
            persistedState={toolStates['podcasts']}
            onStateChange={(state) => handleToolStateChange('podcasts', state)}
            externalAudioSelection={showAudioPlayer ? selectedAudioItem : null}
            externalShowGenerator={toolStates['podcasts']?.showGenerator}
            onClearAudioSelection={() => {
              setShowAudioPlayer(false);
              setSelectedAudioItem(null);
            }}
          />
        );
      
      case 'audiobooks':
        return (
          <AudiobooksTool
            selectedCourse={null}
            isExpanded={true}
            isPanelExpanded={true}
            onClose={() => handleToolChange('chat')}
            persistedState={toolStates['audiobooks']}
            onStateChange={(state) => handleToolStateChange('audiobooks', state)}
            externalAudioSelection={showAudioPlayer ? selectedAudioItem : null}
            externalShowGenerator={toolStates['audiobooks']?.showGenerator}
            onClearAudioSelection={() => {
              setShowAudioPlayer(false);
              setSelectedAudioItem(null);
            }}
          />
        );

      case 'tts':
        return (
          <TextToSpeechTool
            onClose={() => handleToolChange('chat')}
            persistedState={toolStates['tts']}
            onStateChange={(state) => handleToolStateChange('tts', state)}
          />
        );
      
      default:
        return (
          <div className="aitools-placeholder">
            <div className="placeholder-content">
              <div className="placeholder-icon">⚠️</div>
              <h3>Unknown Tool</h3>
              <p>The tool "{activeTool}" is not recognized.</p>
              <button
                className="ui-btn ui-btn--primary"
                onClick={() => handleToolChange('chat')}
              >
                Go to Chat
              </button>
            </div>
          </div>
        );
    }
  };

  // Whether the paywall gate should overlay on top of the tools
  const [gateDismissed, setGateDismissed] = useState(false);
  const showPaywall = initialized && !hasPremiumAccess && !canCreate('chat') && !gateDismissed;

  return (
    <div className="aitools-page">
      <div className={cn('aitools-body')}>
        {/* ── Collapsible Study Pane ── */}
        <aside className={cn('aitools-pane', sidebarCollapsed && 'aitools-pane--collapsed')} ref={paneRef}>
          {sidebarCollapsed ? (
            /* ── Collapsed: compact icon strip ── */
            <div className="aitools-pane__rail">
              {/* Expand pane toggle */}
              <button
                type="button"
                className="aitools-pane__icon-btn"
                onClick={() => setSidebarCollapsed(false)}
                aria-label="Expand pane"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <line x1="9" y1="3" x2="9" y2="21"/>
                  <polyline points="13 9 16 12 13 15"/>
                </svg>
              </button>

              <div className="aitools-pane__divider" />

              {/* Pane actions */}
              <button
                type="button"
                className="aitools-pane__icon-btn"
                onClick={() => { handleToolChange('chat'); handleNewChat(); }}
                aria-label="New chat"
                data-tooltip="New chat"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                </svg>
              </button>
              <button
                type="button"
                className="aitools-pane__icon-btn"
                onClick={() => { setSidebarCollapsed(false); setSearchMode(true); }}
                aria-label="Search chats"
                data-tooltip="Search chats"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </button>

              <div className="aitools-pane__divider" />

              {/* Study tool icons */}
              {TOOLS.map((tool) => (
                <button
                  key={tool.key}
                  type="button"
                  className={cn('aitools-pane__icon-btn', activeTool === tool.key && 'is-active')}
                  onClick={() => handleToolChange(tool.key)}
                  aria-label={tool.label}
                  data-tooltip={tool.label}
                >
                  {tool.icon}
                </button>
              ))}
            </div>
          ) : searchMode ? (
            /* ── Expanded: search mode ── */
            <>
              <div className="aitools-pane__search-bar">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  className="aitools-pane__search-input"
                  placeholder={SEARCH_PLACEHOLDERS[activeTool]}
                  value={paneSearchQuery}
                  onChange={(e) => setPaneSearchQuery(e.target.value)}
                />
                <button
                  type="button"
                  className="aitools-pane__search-close"
                  onClick={() => { setSearchMode(false); setPaneSearchQuery(''); }}
                  aria-label="Close search"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
              <div className="aitools-pane__content">
                {renderHistoryPanel()}
              </div>
            </>
          ) : (
            /* ── Expanded: normal mode ── */
            <>
              {/* Row 1: Workspace title + collapse toggle */}
              <div className="aitools-pane__header">
                <span className="aitools-pane__title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
                  </svg>
                  Scout's Desk
                </span>
                <button
                  type="button"
                  className="aitools-pane__collapse-btn"
                  onClick={() => setSidebarCollapsed(true)}
                  aria-label="Collapse pane"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <line x1="9" y1="3" x2="9" y2="21"/>
                    <polyline points="16 15 13 12 16 9"/>
                  </svg>
                </button>
              </div>

              {/* Control surface: tools + actions */}
              <div className="aitools-pane__control-surface">
                {/* Tools grid */}
                <nav className="aitools-pane__tools" aria-label="Study tools">
                  {TOOLS.map((tool) => (
                    <button
                      key={tool.key}
                      type="button"
                      className={cn('aitools-pane__tool-btn', activeTool === tool.key && 'is-active')}
                      onClick={() => handleToolChange(tool.key)}
                    >
                      <span className="aitools-pane__tool-btn-icon">{tool.icon}</span>
                      <span className="aitools-pane__tool-btn-label">{tool.label}</span>
                    </button>
                  ))}
                </nav>

                {/* Actions */}
                <div className="aitools-pane__actions">
                  <button
                    type="button"
                    className="aitools-pane__action-row"
                    onClick={handleNewItem}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                    </svg>
                    <span>{NEW_ITEM_LABELS[activeTool]}</span>
                  </button>
                  <button
                    type="button"
                    className="aitools-pane__action-row"
                    onClick={() => setSearchMode(true)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <span>{SEARCH_PLACEHOLDERS[activeTool]}</span>
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="aitools-pane__section-divider" />

              {/* History / library panel */}
              <div className="aitools-pane__content">
                {renderHistoryPanel()}
              </div>
            </>
          )}
        </aside>

        {/* ── Workspace ── */}
        <main className="aitools-workspace">
          {showNudge && (
            <div className="course-nudge">
              <div className="course-nudge__inner">
                <div className="course-nudge__scout">
                  <img
                    src="/scout_images/scout_curious.png"
                    alt=""
                    className="course-nudge__scout-img"
                    draggable="false"
                  />
                  <div className="course-nudge__bubble">
                    <p>Upload a course and I&rsquo;ll give you better answers, personalized quizzes, and study plans.</p>
                  </div>
                </div>
                <div className="course-nudge__actions">
                  <button
                    className="course-nudge__cta"
                    onClick={() => navigate('/dashboard')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    Upload a course
                  </button>
                  <button
                    className="course-nudge__dismiss"
                    onClick={dismissNudge}
                  >
                    Maybe later
                  </button>
                </div>
              </div>
            </div>
          )}
          {renderToolContent()}
        </main>
      </div>

      {/* Paywall gate overlay — renders as a modal on top of content */}
      {showPaywall && (
        <div className="tools-gate-overlay" style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ToolsGateScreen navigate={navigate} showUploadNudge={showNudge} onDismissNudge={dismissNudge} onClose={() => setGateDismissed(true)} />
        </div>
      )}

      {/* Upgrade Modal for gated creation actions */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={currentTier}
        requiredTier={blockedFeature.tier}
        featureName={blockedFeature.name}
        featureDescription={`${blockedFeature.name} requires a ${blockedFeature.tier === 'pro' ? 'Pro' : 'Basic'} plan. Upgrade now to unlock this feature.`}
        triggerSource={blockedFeature.tier === 'pro' ? 'audio' : 'quiz'}
      />
    </div>
  );
}
